import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { RoseRow } from "@/design-system/RoseRow";

/**
 * Admin Desk · Rate Event — RATIFIED rubric v1.0 (10 Sep 2026).
 *
 * Staff-only. Rubric-driven end-to-end (family count is data). The rating
 * control is the VERTICAL liquid-fill column with 1 / 3 / 5 anchors visible
 * beside it. There is no mode toggle — the headline is family_mean, per
 * ratified rubric. flat_mean is rendered as a read-only audit line beside it.
 *
 * Public leakage is impossible: `backend/routers/public.py` has an explicit
 * whitelist + `_FORBIDDEN_PUBLIC` guard. The score is never sent to a public
 * surface (not even `null`) until an editorial process reaches `published`
 * AND those serialisers change — both are separate steps that do not exist
 * yet.
 */

const STOPS = [5.0, 4.5, 4.0, 3.5, 3.0, 2.5, 2.0, 1.5, 1.0];   // top-down
const nowIso = () => new Date().toISOString();

function VerticalLiquidColumn({ dim, rating, onChange }) {
  const disabled = rating.set_aside || rating.na;
  const value = rating.absent ? 1.0 : (typeof rating.value === "number" ? rating.value : null);
  const fillPct = value == null ? 0 : ((value - 1) / 4) * 100;    // 1.0 -> 0%, 5.0 -> 100%

  const anchorText = (levelValue) => {
    if (!dim.anchors) return "";
    const idx = Math.round(levelValue - 1);
    return dim.anchors[idx] || "";
  };

  const canSetAside = dim.na === "optional";   // ratified: only food_quality, drinks_pricing
  const anchorFor = (v) => {
    if (v == null) return "";
    if (v <= 1.5) return dim.anchors?.[0] || "";
    if (v <= 2.5) return dim.anchors?.[1] || "";
    if (v <= 3.5) return dim.anchors?.[2] || "";
    if (v <= 4.5) return dim.anchors?.[3] || "";
    return dim.anchors?.[4] || "";
  };

  const step = (delta) => {
    const cur = value ?? 3.0;
    const next = Math.min(5, Math.max(1, +(cur + delta).toFixed(1)));
    onChange({ ...rating, value: next, na: false, set_aside: false, absent: false });
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4" data-testid={`dim-${dim.key}`}>
      <div className="mb-3">
        <p className="text-[15px] font-semibold text-neutral-900">{dim.title}</p>
        {dim.question && <p className="mt-0.5 text-[13px] text-neutral-600">{dim.question}</p>}
        {dim.note && <p className="mt-1 text-[11px] italic text-neutral-500">note. {dim.note}</p>}
      </div>

      <div className="flex gap-4">
        {/* THE COLUMN */}
        <div className="flex items-stretch gap-2">
          <div
            role="slider"
            aria-orientation="vertical"
            aria-valuemin={1}
            aria-valuemax={5}
            aria-valuenow={value ?? 3}
            aria-label={dim.title}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "ArrowUp") { e.preventDefault(); step(+0.5); }
              if (e.key === "ArrowDown") { e.preventDefault(); step(-0.5); }
            }}
            data-testid={`dim-${dim.key}-column`}
            className={`relative w-14 rounded-md border ${disabled ? "opacity-30" : ""}`}
            style={{ height: 240, background: "#F3F1EA", borderColor: "#D8D2C6" }}
          >
            {/* liquid fill (bottom → top) */}
            <div
              aria-hidden
              className="absolute bottom-0 left-0 right-0 rounded-md transition-[height] duration-200"
              style={{
                height: `${fillPct}%`,
                background: value == null ? "transparent" : "linear-gradient(180deg,#FFD60A 0%, #C89600 100%)",
              }}
            />
            {/* 9 stop tick buttons — each is a hit-target */}
            <div className="absolute inset-0 flex flex-col-reverse justify-between px-1 py-1">
              {STOPS.slice().reverse().map((s) => {
                const active = value === s;
                return (
                  <button
                    key={s}
                    type="button"
                    disabled={disabled}
                    data-testid={`dim-${dim.key}-stop-${s}`}
                    onClick={() => onChange({ ...rating, value: s, na: false, set_aside: false, absent: false })}
                    className={`relative h-4 w-full rounded ${active ? "ring-2 ring-neutral-900" : "hover:bg-black/5"}`}
                    aria-label={`${dim.title} — ${s}`}
                    style={{ marginTop: 0 }}
                  >
                    <span className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-center text-[9px] font-semibold text-neutral-700">
                      {[1, 3, 5].includes(s) ? s.toFixed(0) : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Anchors — 5 / 3 / 1 visible beside the column */}
          <div className="flex flex-col justify-between text-[11px] leading-tight text-neutral-600" style={{ width: 220 }}>
            {[5, 3, 1].map((lv) => (
              <div key={lv} data-testid={`dim-${dim.key}-anchor-${lv}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">{lv} · {anchorLabel(lv)}</p>
                <p className="mt-0.5 italic">{anchorText(lv)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right-column controls: current pick + buttons */}
        <div className="flex flex-1 flex-col gap-2">
          <div className="rounded border border-neutral-200 bg-neutral-50 p-2 text-[12px]" data-testid={`dim-${dim.key}-current`}>
            {value != null ? (
              <>
                <p className="text-[11px] uppercase tracking-[0.16em] text-neutral-500">selected</p>
                <p className="text-lg font-semibold">{value.toFixed(1)}</p>
                <p className="mt-1 italic text-neutral-600">{anchorFor(value)}</p>
              </>
            ) : (
              <p className="text-neutral-500">not rated yet</p>
            )}
          </div>

          <div className="flex flex-wrap gap-1 text-[10px]">
            <button
              data-testid={`dim-${dim.key}-absent`}
              className={`rounded-full border px-2 py-0.5 ${rating.absent ? "bg-red-600 text-white border-red-600" : "border-neutral-300"}`}
              onClick={() => onChange({ ...rating, absent: !rating.absent, na: false, set_aside: false, value: !rating.absent ? 1 : null })}
            >should have been here</button>
            {canSetAside && (
              <button
                data-testid={`dim-${dim.key}-setaside`}
                className={`rounded-full border px-2 py-0.5 ${rating.set_aside ? "bg-neutral-500 text-white border-neutral-500" : "border-neutral-300"}`}
                onClick={() => onChange({ ...rating, set_aside: !rating.set_aside, na: false, absent: false, value: null })}
              >set aside</button>
            )}
          </div>

          {rating.absent && (
            <textarea
              data-testid={`dim-${dim.key}-note`}
              value={rating.note || ""}
              onChange={(e) => onChange({ ...rating, note: e.target.value })}
              rows={3}
              placeholder="required — what should have been here?"
              className="w-full rounded border border-red-300 p-2 text-[13px]"
            />
          )}
        </div>
      </div>
    </div>
  );
}

const anchorLabel = (lv) => ({
  1: "got in the way",
  3: "did the job",
  5: "exceptional",
}[lv] || "");

export default function RateEventPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [rubric, setRubric] = useState(null);
  const [event, setEvent] = useState(null);
  const [ratings, setRatings] = useState({});
  const [minStd, setMinStd] = useState({});
  const [notes, setNotes] = useState("");
  const [computed, setComputed] = useState(null);
  const [scoreId, setScoreId] = useState(null);
  const [state, setState] = useState("draft");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [rr, ev, sc] = await Promise.all([
      client.get("/rubric"),
      client.get(`/public/events/${eventId}`).catch(() => ({ data: { event: null } })),
      client.get(`/events/${eventId}/scores`).catch(() => ({ data: { scores: [] } })),
    ]);
    setRubric(rr.data);
    setEvent(ev.data?.event || null);
    const scores = sc.data?.scores || [];
    const draft = scores.find((s) => s.state !== "published") || null;
    if (draft) {
      setScoreId(draft.id);
      setRatings(draft.ratings || {});
      setMinStd(draft.min_standards || {});
      setNotes(draft.notes || "");
      setState(draft.state);
      setComputed(draft.computed || null);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!rubric) return;
    setComputed(computeLocal(rubric, ratings));
  }, [rubric, ratings]);

  const setRating = (key, r) => setRatings((p) => ({ ...p, [key]: r }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ratings, min_standards: minStd, notes };
      const r = scoreId
        ? await client.patch(`/events/${eventId}/scores/${scoreId}`, payload)
        : await client.post(`/events/${eventId}/scores`, payload);
      setScoreId(r.data.id);
      setComputed(r.data.computed);
      setState(r.data.state);
      toast.success("saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "save failed");
    } finally {
      setSaving(false);
    }
  };

  const transition = async (target) => {
    if (!scoreId) { toast.error("save the draft first"); return; }
    try {
      const r = await client.patch(`/events/${eventId}/scores/${scoreId}/state`, { state: target });
      setState(r.data.state);
      toast.success(`moved to ${target}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.failing) toast.error(`cannot publish: ${d.failing.join(", ")}`);
      else toast.error(typeof d === "string" ? d : "transition failed");
    }
  };

  if (!rubric) return <div className="p-8">loading rubric…</div>;

  const msChecks = rubric?.minimum_standards?.checks || [];
  const allMsPass = msChecks.every((c) => !!minStd[c.key]);
  const canPublish = allMsPass && (computed?.headline || 0) > 0;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 p-6" data-testid="rate-event-page">
      <header className="flex items-start justify-between">
        <div>
          <button className="mb-2 text-xs text-neutral-500 hover:underline" onClick={() => navigate(-1)}>← back</button>
          <p className="label-tiny">Rating · phase-one staff only · rubric v{rubric.version}</p>
          <h1 className="mt-1 text-2xl font-display">{event?.title || `event ${eventId}`}</h1>
          <p className="mt-1 text-xs text-neutral-500">
            {event?.venue_display || ""}{event?.start_at ? ` · ${new Date(event.start_at).toLocaleString("en-GB", { timeZone: "Africa/Nairobi" })}` : ""}
          </p>
          <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-neutral-500">
            state · <span data-testid="score-state">{state}</span>{scoreId && <span className="ml-2 text-neutral-400">({scoreId.slice(0, 8)})</span>}
          </p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-4 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-red-600" data-testid="provisional-tag">
            PROVISIONAL — NOT PUBLISHED
          </p>
          <div className="mt-2 flex items-center justify-end gap-2">
            <RoseRow value={computed?.headline || 0} provisional size={24} ground="cream" colourway="ink" />
          </div>
          <p className="mt-1 text-[10px] text-neutral-500" data-testid="headline-mode-info">
            family_mean (ratified) · audit: flat_mean = {computed?.alt_headline ?? "—"}
          </p>
        </div>
      </header>

      {/* Family means strip */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <p className="label-tiny">Per-family means (live) — the headline is the mean of these</p>
        <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-5">
          {(rubric.event.families || []).map((f) => {
            const v = computed?.family_means?.[f.key];
            const dropped = (computed?.dropped_families || []).includes(f.key);
            return (
              <div key={f.key} className="rounded-lg border border-neutral-200 p-2 text-sm" data-testid={`fam-mean-${f.key}`}>
                <p className="truncate text-[11px] uppercase tracking-[0.14em] text-neutral-500">{f.title}</p>
                <p className={`mt-1 text-lg font-semibold ${dropped ? "text-neutral-300" : ""}`}>{dropped ? "not rated" : (v ?? "—")}</p>
                {dropped && <p className="text-[10px] text-red-600">below floor · excluded from headline</p>}
              </div>
            );
          })}
        </div>
      </section>

      {/* Minimum standards */}
      <section className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="min-standards">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-700">Minimum standards · publish gate</p>
        <p className="mt-1 text-xs text-red-700">{rubric.minimum_standards?.note}</p>
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          {msChecks.map((c) => (
            <label key={c.key} className="flex items-start gap-2 rounded border border-red-200 bg-white p-2 text-[13px]" data-testid={`ms-${c.key}`}>
              <input type="checkbox" checked={!!minStd[c.key]} onChange={(e) => setMinStd((p) => ({ ...p, [c.key]: e.target.checked }))} />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Families & dimensions */}
      {(rubric.event.families || []).map((fam) => (
        <section key={fam.key} className="flex flex-col gap-3" data-testid={`family-${fam.key}`}>
          <div>
            <h2 className="text-lg font-display">{fam.title}</h2>
            {fam.note && <p className="text-xs italic text-neutral-500">{fam.note}</p>}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {fam.dimensions.map((dim) => (
              <VerticalLiquidColumn
                key={dim.key}
                dim={dim}
                rating={ratings[dim.key] || {}}
                onChange={(r) => setRating(dim.key, r)}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Notes */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <label className="label-tiny">Scout notes (optional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="score-notes"
                  className="mt-2 w-full rounded border border-neutral-300 p-2 text-sm" />
      </section>

      {/* Actions */}
      <footer className="sticky bottom-0 flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm">
        <button data-testid="score-save" disabled={saving} onClick={save}
                className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
          {saving ? "saving…" : (scoreId ? "save changes" : "save as draft")}
        </button>
        <button data-testid="score-in-review" disabled={!scoreId || state === "published"} onClick={() => transition("in_review")}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm disabled:opacity-40">
          mark ready for review
        </button>
        <button data-testid="score-publish" disabled={!scoreId || !canPublish || state === "published"} onClick={() => transition("published")}
                className="rounded-md border border-neutral-900 bg-neutral-900 px-3 py-2 text-sm text-white disabled:opacity-40"
                title={!canPublish ? "min-standards must all pass and a headline must exist" : "publish this score"}>
          publish
        </button>
        {state === "published" && (
          <button data-testid="score-unpublish" onClick={() => transition("in_review")}
                  className="rounded-md border border-red-400 px-3 py-2 text-sm text-red-600">unpublish</button>
        )}
        <p className="ml-auto text-[10px] text-neutral-500">
          the number never appears on any public surface until this record is `published`
        </p>
      </footer>
    </div>
  );
}

/* Local live compute — mirrors backend/scoring.py. Headline is family_mean only. */
function roundHalfDown(v, step) { return Math.ceil(v / step - 0.5) * step; }
function roundDisplay(v) { return v == null ? null : Number(roundHalfDown(v, 0.1).toFixed(1)); }
function roundRoses(v) { return v == null ? null : Number(roundHalfDown(v, 0.5).toFixed(1)); }
function applicable(r) { if (!r) return false; if (r.na || r.set_aside) return false;
  const v = r.absent ? 1 : r.value; return typeof v === "number" && v > 0; }
function dimVal(r) { if (!applicable(r)) return null; return r.absent ? 1 : r.value; }
function computeLocal(rubric, ratings) {
  const agg = rubric.scale?.aggregation || {};
  const floor = Number(agg.floor_per_family || 2);
  const familyMeans = {}; const dropped = [];
  let flatCount = 0; let flatSum = 0;
  rubric.event.families.forEach((fam) => {
    const vals = fam.dimensions.map((d) => dimVal(ratings[d.key])).filter((v) => v != null);
    if (vals.length < floor) { familyMeans[fam.key] = null; dropped.push(fam.key); return; }
    familyMeans[fam.key] = vals.reduce((a, b) => a + b, 0) / vals.length;
    flatCount += vals.length; flatSum += vals.reduce((a, b) => a + b, 0);
  });
  const lives = Object.values(familyMeans).filter((v) => v != null);
  const family = lives.length ? lives.reduce((a, b) => a + b, 0) / lives.length : null;
  const flat = flatCount > 0 ? flatSum / flatCount : null;
  return {
    mode: "family_mean",
    headline: roundDisplay(family), roses: roundRoses(family),
    alt_mode: "flat_mean", alt_headline: roundDisplay(flat), alt_roses: roundRoses(flat),
    family_means: Object.fromEntries(Object.entries(familyMeans).map(([k, v]) => [k, roundDisplay(v)])),
    dropped_families: dropped, applicable_count: flatCount,
  };
}
